import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDeleteReservationMutation, useUpdateReservationMutation } from '../Service/reservationsApi';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { useGetReservationWithAssociationsByIdQuery } from '../Service/extendedReservationsApi';
import {
  ReservationFormState,
  ReservationPartyLookupDto,
  ReservationPartySelectorField,
} from '../Types/reservationTypes';
import { initialReservationValues } from '../initialValues';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import { ReservationsRoutes } from '../../../Router/path';
import EntityFormLayout from '../../Shared/Components/EntityFormLayout/EntityFormLayout';
import { filterReservationPartiesWithAddNew } from '../Helpers/filterReservationPartiesWithAddNew';
import ParticipantsQuickActions from '../Components/ParticipantsQuickActions/ParticipantsQuickActions';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import { FormField } from '../../Shared/Components/FormRenderer';
import { useReservationFields } from '../Hooks/useReservationFields';
import { useFormHandlers } from '../Hooks/useFormHandlers';
import { useParticipants } from '../Hooks/useParticipants';
import { getReservationPartyLookupIds } from '../Helpers/getReservationPartyLookupIds';
import { getOfferIdFromBookings } from '../Helpers/getOfferIdFromBookings';
import { useReservationBookingContext } from '../Hooks/useReservationBookingContext';
import { useGetAllForOfferQuery } from '../../OfferBoardingPlaces/Service/extendedOfferBoardingPlacesApi';
import { submitReservationForm } from '../Helpers/submitReservationForm';
import { buildOptionTypesForField, toMappingIds } from '../Helpers/offerOptionsUtils';
import {
  updateReservationPartySearchState,
  type ReservationPartySearchReason,
} from '../Helpers/updateReservationPartySearchState';
import ReservationPricePreview from '../Components/ReservationPricePreview';
import { ReservationPaymentHistorySection } from '../Components';
import { ContractPdfActions } from '../../Contracts';
import ContractGenerateButton from '../../Contracts/Components/ContractGenerateButton';
import { CashDocumentsSection } from '../../CashDocuments';
import { Alert, Box, Button } from '@mui/material';
import {
  useFindOrCreateReservationPartyMutation,
  useSearchableReservationPartiesLookupQuery,
} from '../Service/reservationPartiesLookupApi';
import { ReservationHeadcountValidationAlert, ReservationRoleSummary } from '../Components';
import AddReservationPartyModal from '../Components/AddReservationPartyModal';
import ReservationNotesSection from '../Components/ReservationNotesSection';
import { mapReservationPartyDtoToLookup } from '../Service/reservationPartiesLookupMapper';
import type { ReservationPartyLookup } from '../Service/reservationPartiesLookupMapper';
import type { ReservationFormChangeEvent } from '../Types/reservationFormChange';
import { useGetByOfferIdQuery } from '../../OfferOptionsMappings/Service/extendedOfferOptionsMappingsApi';
import { useGetOfferOptionTypesQuery } from '../../OfferOptions/Service/extendedOferOptionsApi';
import { OfferOptionUsageType, OfferOptionType } from '../../OfferOptionsMappings/Types/offerOptionsMappingsTypes';
import { useReservationLocalPrices } from '../Hooks/useReservationLocalPrices';

const MAX_PARTICIPANTS = 5;
const MIN_SEARCH_LENGTH = 3;

const initialReservationPartySearchState: Record<ReservationPartySelectorField, string> = {
  applicantPartyId: '',
  invoiceBuyerPartyId: '',
  fundingPartyId: '',
};

const UpdateReservation = () => {
  // === PARAMS ===
  const { reservationId } = useParams<{ reservationId: string }>();
  const parsedId = reservationId ? parseInt(reservationId, 10) : null;

  // === MUTATIONS ===
  const [updateReservation, updateStatus] = useUpdateReservationMutation();
  const [findOrCreateReservationParty] = useFindOrCreateReservationPartyMutation();
  const [cancelReservation, { isLoading: isCancelling }] = useDeleteReservationMutation();
  const [cancelError, setCancelError] = useState<string>('');

  // === FORM STATE ===
  const [formData, setFormData] = useState<ReservationFormState>(initialReservationValues);
  const [autocompleteResetKeys, setAutocompleteResetKeys] = useState<Record<string, number>>({});
  const [addPartyModalOpen, setAddPartyModalOpen] = useState(false);
  const [activePartyField, setActivePartyField] = useState<ReservationPartySelectorField>('applicantPartyId');
  const [additionalParties, setAdditionalParties] = useState<ReservationPartyLookup[]>([]);

  // === LOOKUPS ===
  const {
    selectedBookingContext,
    bookingLookupOptions,
    selectedBookingLoading,
    bookingLookupLoading,
    handleBookingLookupInputChange,
  } = useReservationBookingContext({
    bookingId: Number(formData.bookingId) || null,
  });
  const [activeReservationPartySearchField, setActiveReservationPartySearchField] =
    useState<ReservationPartySelectorField>('applicantPartyId');
  const [anyPartyDropdownOpened, setAnyPartyDropdownOpened] = useState(false);
  const [reservationPartySearches, setReservationPartySearches] = useState(
    initialReservationPartySearchState
  );
  const activeReservationPartySearch =
    reservationPartySearches[activeReservationPartySearchField];
  const reservationPartyLookupIds = getReservationPartyLookupIds(formData);
  const partySearchEnabled =
    anyPartyDropdownOpened ||
    activeReservationPartySearch.trim().length >= MIN_SEARCH_LENGTH ||
    reservationPartyLookupIds.length > 0;
  const partyQueryArgs = useMemo(
    () => ({
      search: activeReservationPartySearch.trim() || undefined,
      ids: reservationPartyLookupIds.length > 0 ? reservationPartyLookupIds : undefined,
      take: activeReservationPartySearch.trim() ? 200 : 20,
    }),
    [activeReservationPartySearch, reservationPartyLookupIds]
  );
  const { parties: lookupParties, isLoading: reservationPartiesLoading } =
    useSearchableReservationPartiesLookupQuery(partyQueryArgs, !partySearchEnabled);
  const allParties = useMemo(() => {
    const seen = new Set<number>();
    return [...lookupParties, ...additionalParties].filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [lookupParties, additionalParties]);
  const { data: reservationData, isLoading: isReservationLoading } =
    useGetReservationWithAssociationsByIdQuery(parsedId!, { skip: !parsedId });

  useEffect(() => {
    if (reservationData) {
      setFormData({
        ...initialReservationValues,
        ...reservationData,
        selectedOfferOptions:
          reservationData.selectedOptions
            ?.filter((option) => option.usageType === OfferOptionUsageType.Additional)
            .map((option) => ({
              offerOptionsTypeId: option.offerOptionsTypeId,
              offerOptionsValueId: option.offerOptionsValueId,
            })) ?? [],
      });
    }
  }, [reservationData]);

  const createReservationPartySearchHandler = useCallback(
    (fieldName: ReservationPartySelectorField) =>
    (inputValue: string, reason?: ReservationPartySearchReason) => {
      if (reason === 'open') {
        setActiveReservationPartySearchField(fieldName);
        setAnyPartyDropdownOpened(true);
        return;
      }
      setReservationPartySearches((currentSearches) => {
        const nextState = updateReservationPartySearchState({
          currentSearches,
          fieldName,
          inputValue,
          reason,
        });

        setActiveReservationPartySearchField(nextState.activeFieldName);

        return nextState.searches;
      });
    },
    []
  );

  // === LOOKUPS ===
  const offerId = getOfferIdFromBookings(selectedBookingContext);
  const { data: offerBoardingPlaces, isLoading: offerBoardingPlacesLoading } =
    useGetAllForOfferQuery(offerId ?? 0, {
      skip: !offerId, // skip query if offerId is not available
    });
  const { data: offerOptionsMappings, isLoading: offerOptionsMappingsLoading } = useGetByOfferIdQuery(
    offerId ?? 0,
    { skip: !offerId }
  );
  const { data: offerOptionTypes, isLoading: offerOptionTypesLoading } = useGetOfferOptionTypesQuery();
  const previousOfferIdRef = useRef<number | null | undefined>(undefined);
  const mappedOfferOptionTypes: OfferOptionType[] = useMemo(
    () =>
      (offerOptionTypes ?? []).map(({ id, name, offerOptionsValues }) => ({
        id,
        name,
        offerOptionsValues,
      })),
    [offerOptionTypes]
  );
  const additionalOptionTypes = useMemo(
    () => buildOptionTypesForField(mappedOfferOptionTypes, offerOptionsMappings ?? []),
    [mappedOfferOptionTypes, offerOptionsMappings]
  );
  const hasOfferOptions = useMemo(
    () =>
      (offerOptionsMappings ?? []).some(
        (mapping) => mapping.usageType === OfferOptionUsageType.Additional
      ),
    [offerOptionsMappings]
  );

  // === DYNAMIC FORM FIELDS ===
  const dynamicFields = useReservationFields({
    formData,
    bookingLookupOptions,
    bookingLookupLoading: bookingLookupLoading || selectedBookingLoading,
    reservationParties: allParties,
    reservationPartiesLoading,
    offerBoardingPlaces: offerBoardingPlaces ?? [],
    offerBoardingPlacesLoading,
    offerOptionTypes: additionalOptionTypes,
    offerOptionTypesLoading: offerOptionsMappingsLoading || offerOptionTypesLoading,
    hasOfferOptions,
  });

  const fieldsWithReservation = useMemo(
    () => dynamicFields as FormField<ReservationFormState>[],
    [dynamicFields]
  );

  // === FORM HANDLERS ===
  const { handleChange } = useFormHandlers(setFormData);
  const handleChangeWithModal = useCallback(
    (e: ReservationFormChangeEvent, child?: React.ReactNode) => {
      const { name, value } = e.target;
      if (!name) {
        handleChange(e, child);
        return;
      }
      const PARTY_FIELDS: ReservationPartySelectorField[] = [
        'applicantPartyId',
        'invoiceBuyerPartyId',
        'fundingPartyId',
      ];
      if (PARTY_FIELDS.includes(name as ReservationPartySelectorField) && value === '__new__') {
        setAutocompleteResetKeys((k) => ({ ...k, [name]: (k[name] ?? 0) + 1 }));
        setActivePartyField(name as ReservationPartySelectorField);
        setAddPartyModalOpen(true);
        return;
      }
      handleChange(e, child);
    },
    [handleChange]
  );
  const handlePartyCreated = useCallback(
    (party: ReservationPartyLookupDto, field: ReservationPartySelectorField) => {
      const lookup = mapReservationPartyDtoToLookup(party);
      if (lookup) {
        setAdditionalParties((prev) => [...prev, lookup]);
      }
      handleChange(
        {
          target: { name: field, value: party.id },
        },
        undefined
      );
    },
    [handleChange]
  );

  // Handlery do participantów (użycie helpera)
  const {
    handleAddEmptyParticipant,
    handleAddParticipantFromApplicant,
    maxReached,
    disabledFromApplicant,
  } = useParticipants({
    formData,
    setFormData,
    applicantById: undefined,
    today: new Date().toISOString().slice(0, 10),
    maxParticipants: MAX_PARTICIPANTS,
    applicantLoading: false,
  });

  // === SUBMIT ===
  const entityForm = useEntityForm<ReservationFormState>({
    initialData: formData,
    fields: fieldsWithReservation,
    submitFn: async (data) =>
      submitReservationForm({
        data,
        formData: data as ReservationFormState,
        createdReservationId: parsedId,
        setCreatedReservationId: () => {
          // No-op: update flow never creates a new reservation id.
        },
        createReservation: async () => {
          throw new Error('Create mutation is not supported in UpdateReservation flow.');
        },
        updateReservation: (payload) => updateReservation(payload).unwrap(),
        findOrCreateReservationParty,
        omitPaymentFieldsOnUpdate: true,
        offerOptionsMappings: offerOptionsMappings ?? [],
        offerOptionsMappingsLoading,
      }),
    redirectUrl: ReservationsRoutes.root,
    mutationStatus: updateStatus,
    groupOrder: [
      FormRenderGroupTypes.basic,
      FormRenderGroupTypes.participants,
    ],
  });

  // === WATCH PAYMENT FIELDS FOR REAL-TIME UPDATES ===
  // Use entityForm.formState.formData instead of local formData
  // because useEntityForm manages its own internal state
  const entityFormData = entityForm.formState.formData;
  const selectedAdditionalOptionMappingIds = useMemo(
    () => toMappingIds(entityFormData.selectedOfferOptions ?? [], offerOptionsMappings ?? []),
    [entityFormData.selectedOfferOptions, offerOptionsMappings]
  );
  const boardingPlacesPreview = useMemo(
    () =>
      offerBoardingPlaces?.map((bp) => ({
        id: bp.id,
        name: bp.city,
        price: bp.price,
      })) ?? [],
    [offerBoardingPlaces]
  );
  const { localUnitPrice, localBoardingPlacePrice } = useReservationLocalPrices({
    selectedBookingContext,
    offerBoardingPlaces: boardingPlacesPreview,
    offerBoardingPlaceId: Number(entityFormData.offerBoardingPlaceId) || null,
  });

  useEffect(() => {
    if (previousOfferIdRef.current !== undefined && previousOfferIdRef.current !== offerId) {
      setFormData((prev) => ({
        ...prev,
        selectedOfferOptions: [],
      }));
      entityForm.setFormData((prev) => ({
        ...prev,
        selectedOfferOptions: [],
      }));
    }
    previousOfferIdRef.current = offerId;
  }, [offerId, entityForm]);

  const handleAddNote = useCallback((content: string) => {
    // Write through the canonical outer `formData`, not `entityForm`'s internal copy:
    // useEntityForm resyncs its internal state from `initialData` (= this outer formData)
    // on every reference change, so a note added only to the internal state is silently
    // dropped the next time any other field changes and re-triggers that resync.
    setFormData((prev) => ({
      ...prev,
      notes: [...prev.notes, { content }],
    }));
  }, []);

  const handleCancelReservation = async () => {
    if (!parsedId) return;
    if (!window.confirm('Czy na pewno chcesz anulować tę rezerwację?')) {
      return;
    }

    try {
      setCancelError('');
      await cancelReservation(parsedId).unwrap();
    } catch (error) {
      setCancelError(extractApiErrorMessage(error, 'Nie udało się anulować rezerwacji.'));
    }
  };

  if (isReservationLoading) return <div>Ładowanie...</div>;

  const isCancelled = reservationData?.status === 'cancelled';

  if (isCancelled) {
    return (
      <>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Ta rezerwacja została anulowana. Podgląd jest dostępny tylko do odczytu.
        </Alert>
        <ReservationRoleSummary
          applicantPartyId={Number(entityFormData.applicantPartyId) || undefined}
          invoiceBuyerPartyId={Number(entityFormData.invoiceBuyerPartyId) || undefined}
          fundingPartyId={Number(entityFormData.fundingPartyId) || undefined}
          parties={allParties}
        />
        {parsedId !== null && (
          <>
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <ContractPdfActions
                reservationId={parsedId}
                contractName={`Rezerwacja_${parsedId}.pdf`}
              />
            </Box>
            <Box sx={{ mt: 3 }}>
              <ReservationPaymentHistorySection
                reservationId={parsedId}
                totalPrice={entityFormData.totalPrice || 0}
              />
            </Box>
            <Box sx={{ mt: 3 }}>
              <CashDocumentsSection reservationId={parsedId} showCreateButton={false} />
            </Box>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <ReservationRoleSummary
        applicantPartyId={Number(entityFormData.applicantPartyId) || undefined}
        invoiceBuyerPartyId={Number(entityFormData.invoiceBuyerPartyId) || undefined}
        fundingPartyId={Number(entityFormData.fundingPartyId) || undefined}
        parties={allParties}
      />
      <ReservationHeadcountValidationAlert
        participantsCount={Number(entityFormData.participants?.length) || 0}
        declaredParticipantsCount={Number(entityFormData.declaredParticipantsCount) || 0}
      />
      <ReservationPricePreview
        bookingId={Number(entityFormData.bookingId)}
        selectedBoardingPlaceId={Number(entityFormData.offerBoardingPlaceId)}
        numberOfParticipants={
          Number(entityFormData.participants?.length) > 0
            ? Number(entityFormData.participants?.length)
            : Number(entityFormData.declaredParticipantsCount) || 0
        }
        localUnitPrice={localUnitPrice}
        localBoardingPlacePrice={localBoardingPlacePrice}
        selectedAdditionalOptionMappingIds={selectedAdditionalOptionMappingIds}
        discountAmount={Number(entityFormData.discountAmount) || null}
        totalPriceOverride={Number(entityFormData.totalPriceOverride) || null}
      />

      <AddReservationPartyModal
        open={addPartyModalOpen}
        field={activePartyField}
        onClose={() => setAddPartyModalOpen(false)}
        onCreated={handlePartyCreated}
      />
      <EntityFormLayout
        {...entityForm}
        formHandlers={{
          ...entityForm.formHandlers,
          onChange: handleChangeWithModal,
        }}
        customFilterOptions={{
          bookingId: (options) => options,
          applicantPartyId: filterReservationPartiesWithAddNew,
          invoiceBuyerPartyId: filterReservationPartiesWithAddNew,
          fundingPartyId: filterReservationPartiesWithAddNew,
        }}
        autocompleteInputChangeHandlers={{
          bookingId: handleBookingLookupInputChange,
          applicantPartyId: createReservationPartySearchHandler('applicantPartyId'),
          invoiceBuyerPartyId: createReservationPartySearchHandler('invoiceBuyerPartyId'),
          fundingPartyId: createReservationPartySearchHandler('fundingPartyId'),
        }}
        autocompleteResetKeys={autocompleteResetKeys}
        participantsActions={
          <ParticipantsQuickActions
            onAddEmpty={handleAddEmptyParticipant}
            onAddFromApplicant={handleAddParticipantFromApplicant}
            disabledFromApplicant={disabledFromApplicant}
            maxReached={maxReached}
            showAddFromApplicant={false}
          />
        }
      >
        {parsedId !== null && (
          <>
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <ContractGenerateButton reservationId={parsedId} templateId={1} />
              <ContractPdfActions
                reservationId={parsedId}
                contractName={`Rezerwacja_${parsedId}.pdf`}
              />
              <Button
                variant="outlined"
                color="error"
                disabled={isCancelling}
                onClick={handleCancelReservation}
              >
                Anuluj rezerwację
              </Button>
            </Box>
            {cancelError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {cancelError}
              </Alert>
            )}

            {/* Payment History Section */}
            <Box sx={{ mt: 3 }}>
              <ReservationPaymentHistorySection
                reservationId={parsedId}
                totalPrice={entityFormData.totalPrice || 0}
              />
            </Box>

            {/* Cash Documents Section */}
            {/* Cash Documents Section */}
            <Box sx={{ mt: 3 }}>
              <CashDocumentsSection reservationId={parsedId} showCreateButton={true} />
            </Box>

            <ReservationNotesSection notes={entityFormData.notes} onAddNote={handleAddNote} />
          </>
        )}
      </EntityFormLayout>
    </>
  );
};

export default UpdateReservation;
